// ════════════════════════════════════════════════════════════════════════
//  Fehlende Schlüssel – aber nur, wenn etwas daran hängt
//
//  `checkKeysKept` meldet jeden Schlüssel der veröffentlichten Fassung, der im
//  Entwurf fehlt. Das war die alte Regel: einmal veröffentlicht, nie wieder
//  entfernbar. Gemeint ist aber „an diesem Schlüssel hängt der Fortschritt eines
//  Kindes“, und das ist etwas anderes.
//
//  Gefiltert wird HIER und nicht in `packages/lehrgang`: `checkKeysKept` prüft
//  ein Bundle gegen ein anderes und kennt keine Datenbank – wie `checkBundle`.
//  Derselbe Schnitt wie bei `checkForeignKeys`.
// ════════════════════════════════════════════════════════════════════════

import { checkKeysKept, type Bundle, type Problem } from "@otto/lehrgang";

/**
 * Welche Schlüssel stehen an einem Hinweis auf dem Spiel?
 *
 * Zugeordnet wird über eine Tabelle der Pfade, nicht durch Zerlegen des Pfades
 * am Punkt: ein Schlüssel DARF einen Punkt enthalten (`^[a-z0-9]+([-.][a-z0-9]+)*$`),
 * und ein Zerlegen läge dann still daneben. Die Pfade entstehen ohnehin aus
 * denselben Schlüsseln, aus denen wir sie hier wieder zusammensetzen.
 */
function keysAtStake(published: Bundle): Map<string, readonly string[]> {
  const map = new Map<string, readonly string[]>();

  for (const lesson of published.lessons) {
    const stageKeys = lesson.stages.map((s) => s.key);
    // Fällt die ganze Lektion weg, fallen ihre Stufen mit – und nur an denen
    // kann Fortschritt hängen. Ein Lektionsschlüssel für sich trägt keinen.
    map.set(`lessons.${lesson.key}`, stageKeys);
    for (const stage of lesson.stages) {
      map.set(`lessons.${lesson.key}.${stage.key}`, [stage.key]);
    }
  }

  if (published.assessment) {
    map.set("assessment", [published.assessment.key]);
  }

  return map;
}

/**
 * `checkKeysKept`, aber ohne die Hinweise zu Schlüsseln, an denen niemand geübt
 * hat.
 *
 * @param inUse Schlüssel mit Ergebnis, aus `stage_keys_in_use`. Leer, solange
 *              sie nicht geladen sind – dann bleibt kein Hinweis stehen, und
 *              das ist Absicht: gesperrt wird ohnehin am Trigger, und ein
 *              Hinweis ohne Datenlage behauptete etwas.
 */
export function keysKeptProblems(
  published: Bundle,
  draft: Bundle,
  inUse: ReadonlySet<string>,
): readonly Problem[] {
  const stake = keysAtStake(published);

  return checkKeysKept(published, draft).filter((problem) => {
    const keys = stake.get(problem.path);
    // Ein Pfad, den wir nicht zuordnen können, bleibt stehen. Lieber ein
    // Hinweis zu viel als eine Sperre, die still verschwindet, weil
    // `checkKeysKept` einmal einen Pfad anders bildet.
    if (!keys) return true;
    return keys.some((key) => inUse.has(key));
  });
}
